import { Weapon } from '../../Weapon.js';
import { FUSED_WEAPONS } from '../../../../../config/constants.js';

/**
 * Halo (Harmonics fusion: Umbral Lantern + Orbit Ward) — an orbiting ring of
 * light that clips incoming bullets and lights the field.
 *
 * Orbit Ward's persistent-shard machinery, but the shards are lanterns: they
 * carry `blocksBullets` (the utility Orbit Ward gave up — buying it back is
 * part of the fusion's payoff, alongside the freed weapon slot) and the
 * Lantern's `lightRadius`, which Game._drawGloaming reads to widen the lit
 * disc in a darkness fight. It is the definitive Act II defensive pick: a
 * build that can see, and that eats a wall of fire while it does.
 */
export class Halo extends Weapon {
  constructor() {
    super(FUSED_WEAPONS.halo);
    this._shards = [];
  }

  /** How far the ring lights the dark (see Game._drawGloaming). */
  get lightRadius() {
    return this.config.lightRadius + this.level * 4;
  }

  dispose() {
    for (const shard of this._shards) shard.active = false;
    this._shards = [];
  }

  /** Like Orbit Ward and Nova Ward, not timer-fired — the ring stands. */
  update(dt, origin, _target, pool) {
    this.fireFlash = Math.max(0, this.fireFlash - dt);
    this._shards = this._shards.filter((s) => s.active);

    // Same fold-the-fire-rate-into-the-hit-cooldown trick Orbit Ward uses:
    // a standing hazard has no fire timer for the scaled dt to speed up, so
    // "+fire rate" would otherwise do nothing at all for this weapon.
    const fireRateMultiplier = (origin.surgeFireRateMultiplier ?? 1) * (origin.fireRateMultiplier ?? 1);
    const hitCooldownDuration = this.config.hitCooldownDuration / fireRateMultiplier;
    const damage = this.config.shardDamage + this.level * this.config.shardDamagePerLevel;

    while (this._shards.length < this.config.shardCount) {
      this._shards.push(pool.fire({
        x: origin.x,
        y: origin.y,
        radius: this.config.projectileRadius,
        damage,
        hits: 1e9,
        hitCooldownDuration,
        lifetime: 1e9,
        color: this.config.color,
        blocksBullets: this.config.blocksBullets,
        knocksBack: true,
        knockbackForce: 12,
        orbitCenter: origin,
        orbitRadius: this.config.orbitRadius,
        orbitAngle: 0,
        orbitAngularSpeed: this.config.angularSpeed,
      }));
    }

    // Every-frame re-sync, so a level-up never leaves the ring uneven.
    if (this._shards.length > 0) {
      const anchorAngle = this._shards[0].orbitAngle;
      this._shards.forEach((shard, i) => {
        shard.orbitRadius = this.config.orbitRadius + this.level;
        shard.orbitAngle = anchorAngle + (i / this._shards.length) * Math.PI * 2;
        shard.damage = damage;
        shard.hitCooldownDuration = hitCooldownDuration;
      });
    }
  }
}
